






















































import { Vue, Component, Prop } from 'vue-property-decorator';
import moment from 'moment';
import Toast from '@common/helpers/toast';
import { TreatmentEpisodeMemo } from '@/models/treatment-episode';
import { updateTreatmentEpisodeMemoApi } from '@common/services/treatment-episodes';

// Mirrors BaseRequest::MAX_LENGTH_LONG_TEXT on the API — the textarea stops the
// typist at the same point the server would reject them.
const MEMO_MAX_LENGTH = 60000;

/**
 * The memo on one 治療履歴 log (No.198). Rendered only for Sadmin / doctor / CS,
 * who are the only roles that may read or write it — the parent decides that,
 * so everyone who sees this box can edit it.
 */
@Component({})
export default class TreatmentEpisodeMemoBox extends Vue {
  @Prop({ required: true }) patientId!: number | string;
  @Prop({ required: true }) episodeId!: number;
  @Prop({ default: null }) memo!: TreatmentEpisodeMemo | null;

  private isEditing: boolean = false;
  private isSaving: boolean = false;
  private draft: string = '';
  private maxLength = MEMO_MAX_LENGTH;

  get lastEditorName(): string {
    const editor = this.memo && (this.memo.updated_by || this.memo.created_by);
    return editor ? editor.name : '—';
  }

  private created(): void {
    window.addEventListener('beforeunload', this.warnUnsavedDraft);
  }

  private beforeDestroy(): void {
    window.removeEventListener('beforeunload', this.warnUnsavedDraft);
  }

  /**
   * Same shape as reloadHandler in views/settings/posts/create.vue: the
   * listener stays attached and reads the dirty state, so save, cancel and
   * teardown do not each have to remember to detach it.
   *
   * addEventListener rather than assigning window.onbeforeunload, because one
   * of these is mounted per episode panel and an assignment would let the
   * last one mounted clobber the others.
   */
  private warnUnsavedDraft(event: BeforeUnloadEvent): void {
    if (!this.hasUnsavedDraft) {
      return;
    }
    event.preventDefault();
    event.returnValue = '';
  }

  get hasUnsavedDraft(): boolean {
    return this.isEditing && this.draft !== this.savedContent;
  }

  private get savedContent(): string {
    return (this.memo && this.memo.content) || '';
  }

  private startEditing(): void {
    // Defensive ?? on content: it arrives over the network, and a null would
    // make draft.length throw in the template and take the whole panel down.
    this.draft = this.savedContent;
    this.isEditing = true;
  }

  private cancelEditing(): void {
    this.isEditing = false;
    this.draft = '';
  }

  private async save(): Promise<void> {
    if (this.isSaving) {
      return;
    }
    this.isSaving = true;
    try {
      const saved = await updateTreatmentEpisodeMemoApi(
        this.patientId,
        this.episodeId,
        this.draft,
      );
      // The parent owns the episode list, so it applies the new value; keeping
      // a local copy here would drift once the history is refetched.
      this.$emit('saved', { episodeId: this.episodeId, memo: saved });
      this.isEditing = false;
      this.draft = '';
      Toast.success('更新しました');
    } catch (error) {
      Toast.error(this.saveErrorMessage(error));
    } finally {
      this.isSaving = false;
    }
  }

  /**
   * Toast.error only understands a string, a plain object carrying `message`,
   * or an array. An axios failure is an Error, which it matches against none
   * of those and drops silently — passing the error straight in showed the
   * user nothing at all. This pulls out the API's own message, the way the
   * rest of the admin does it (Chat.vue, KarteInfo.vue), and keeps a fallback
   * for a request that never reached the server.
   */
  private saveErrorMessage(error: unknown): string {
    const response = (error as any)?.response;

    return (response && response.data && response.data.message)
      || '更新に失敗しました';
  }

  private formatDateTime(value: string | null): string {
    if (!value) {
      return '';
    }
    const m = moment(value);
    return m.isValid() ? m.format('YYYY年MM月DD日 HH:mm') : '';
  }
}
